import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, startGoogleLogin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Link2, FileText, Youtube, ScrollText, Moon, Sun, Sparkles, LogOut,
  Clock, ArrowRight, Loader2, Upload, Trash2, BookOpen, Mic, MessageSquare
} from "lucide-react";
import SummaryView from "@/components/SummaryView";
import FloatingBot from "@/components/FloatingBot";
import MeetingRecorder from "@/components/MeetingRecorder";
import LanguageToggle from "@/components/LanguageToggle";
import InstallPWAButton from "@/components/InstallPWAButton";
import { useLang } from "@/context/LanguageContext";

const MODES = [
  { key: "url", label: "URL", icon: Link2, testId: "tab-url" },
  { key: "pdf", label: "PDF", icon: FileText, testId: "tab-pdf" },
  { key: "text", label: "Text", icon: ScrollText, testId: "tab-text" },
  { key: "youtube", label: "YouTube", icon: Youtube, testId: "tab-youtube" },
  { key: "meeting", label: "Meeting", icon: Mic, testId: "tab-meeting" },
];

const MODELS = [
  { key: "claude", label: "Claude Sonnet 4.5", tagline: "Deep & literary" },
  { key: "gemini", label: "Gemini 3.1 Pro", tagline: "Fast & analytical" },
];

export default function Dashboard() {
  const { user, loading, logout, setUser, refresh } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  // Seed AuthContext from AuthCallback's navigation state if it's ahead of the context
  useEffect(() => {
    if (!user && location.state?.user) {
      setUser?.(location.state.user);
    }
  }, [user, location.state, setUser]);

  const [mode, setMode] = useState("url");
  const [modelKey, setModelKey] = useState("claude");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (location.state?.user) return; // AuthCallback just delivered us here
    navigate("/", { replace: true });
  }, [loading, user, navigate, location.state]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/summaries");
      setHistory(data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => { if (user || location.state?.user) loadHistory(); }, [user, location.state]);

  const runSummarize = async () => {
    if (busy) return;
    setBusy(true);
    setCurrent(null);
    try {
      let res;
      if (mode === "url") {
        if (!url.trim()) return toast.error("Paste a URL first.");
        res = await api.post("/summarize", { mode: "url", content: url.trim(), model: modelKey });
      } else if (mode === "youtube") {
        if (!ytUrl.trim()) return toast.error("Paste a YouTube URL first.");
        res = await api.post("/summarize", { mode: "youtube", content: ytUrl.trim(), model: modelKey });
      } else if (mode === "text") {
        if (text.trim().length < 50) return toast.error("Paste at least 50 characters of text.");
        res = await api.post("/summarize", { mode: "text", content: text, title, model: modelKey });
      } else if (mode === "pdf") {
        if (!pdfFile) return toast.error("Choose a PDF first.");
        const form = new FormData();
        form.append("file", pdfFile);
        res = await api.post(`/summarize/pdf?model=${encodeURIComponent(modelKey)}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setCurrent(res.data);
      loadHistory();
      toast.success("Summary ready.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Failed to summarize.");
    } finally {
      setBusy(false);
    }
  };

  const openSummary = async (id) => {
    try {
      const { data } = await api.get(`/summaries/${id}`);
      setCurrent(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Failed to load summary.");
    }
  };

  const deleteSummary = async (id) => {
    try {
      await api.delete(`/summaries/${id}`);
      setHistory((h) => h.filter((s) => s.id !== id));
      if (current?.id === id) setCurrent(null);
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  // Effective user — falls back to AuthCallback's handoff state until AuthContext syncs
  const effectiveUser = user || location.state?.user;

  if (loading && !effectiveUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-primary" />
      </div>
    );
  }

  if (!effectiveUser) return null;

  return (
    <div className="min-h-screen bg-background paper-grain">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
              <Sparkles size={16} />
            </div>
            <span className="font-display text-xl tracking-tight">Readlet</span>
          </div>
          <div className="flex items-center gap-2" data-testid="user-menu">
            <Link to="/chat" data-testid="open-chat-btn" className="h-10 px-3.5 rounded-full border border-primary/40 bg-primary/5 text-foreground hover:bg-primary/10 transition-colors flex items-center gap-2 text-sm">
              <MessageSquare size={15} />
              <span className="hidden sm:inline">{lang === "vi" ? "Trò chuyện" : "Chat"}</span>
            </Link>
            <InstallPWAButton />
            <LanguageToggle />
            <button
              data-testid="theme-toggle-btn"
              onClick={toggle}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-accent transition-colors duration-200"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="hidden md:flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border border-border">
              {effectiveUser.picture ? (
                <img src={effectiveUser.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/20" />
              )}
              <span className="text-sm">{effectiveUser.name}</span>
              <button data-testid="logout-btn" onClick={logout} className="ml-1 w-7 h-7 rounded-full flex items-center justify-center hover:bg-accent" title="Sign out">
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-12 gap-8">
        {/* Left column */}
        <section className="lg:col-span-8 space-y-8">
          <div>
            <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-3">
              {t("dash.hello")}, {effectiveUser.name.split(" ")[0]}
            </p>
            <h1 className="text-4xl sm:text-5xl leading-tight mb-3">{t("dash.title")}</h1>
            <p className="text-muted-foreground text-lg">{t("dash.subtitle")}</p>
          </div>

          <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-5 h-auto p-1.5 bg-muted rounded-full" data-testid="mode-tablist">
                {MODES.map((m) => (
                  <TabsTrigger
                    key={m.key}
                    value={m.key}
                    data-testid={m.testId}
                    className="rounded-full py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2"
                  >
                    <m.icon size={15} strokeWidth={1.75} />
                    <span className="hidden sm:inline">{m.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="url" className="mt-6 space-y-4">
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Article or page URL</label>
                <Input
                  data-testid="url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/long-article"
                  className="h-12 rounded-xl"
                />
              </TabsContent>

              <TabsContent value="pdf" className="mt-6">
                <label htmlFor="pdf-input" className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-2xl p-10 cursor-pointer hover:border-primary transition-colors">
                  <Upload className="mb-3 text-muted-foreground" />
                  <p className="text-sm text-foreground mb-1">
                    {pdfFile ? pdfFile.name : "Click to choose a PDF"}
                  </p>
                  <p className="text-xs text-muted-foreground">Up to 15 MB</p>
                  <input
                    data-testid="pdf-input"
                    id="pdf-input"
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  />
                </label>
              </TabsContent>

              <TabsContent value="text" className="mt-6 space-y-3">
                <Input
                  data-testid="text-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give it a title (optional)"
                  className="h-12 rounded-xl"
                />
                <Textarea
                  data-testid="text-area-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste your text here…"
                  className="min-h-[220px] rounded-xl leading-relaxed"
                />
              </TabsContent>

              <TabsContent value="youtube" className="mt-6 space-y-4">
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">YouTube URL</label>
                <Input
                  data-testid="yt-input"
                  value={ytUrl}
                  onChange={(e) => setYtUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=…"
                  className="h-12 rounded-xl"
                />
              </TabsContent>

              <TabsContent value="meeting" className="mt-2">
                <MeetingRecorder
                  modelKey={modelKey}
                  language={lang}
                  onDone={(data, err) => {
                    if (err) { toast.error(err); return; }
                    if (data) {
                      setCurrent(data);
                      loadHistory();
                      toast.success(lang === "vi" ? "Bản tóm tắt cuộc họp đã sẵn sàng." : "Meeting summary ready.");
                    }
                  }}
                />
              </TabsContent>
            </Tabs>

            {mode !== "meeting" && (
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap" data-testid="model-picker">
                <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground mr-1">{t("dash.model")}</span>
                {MODELS.map((m) => (
                  <button
                    key={m.key}
                    data-testid={`model-${m.key}`}
                    onClick={() => setModelKey(m.key)}
                    className={`text-sm rounded-full px-3.5 h-9 border transition-colors duration-200 ${
                      modelKey === m.key
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
                    }`}
                    aria-pressed={modelKey === m.key}
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle ${modelKey === m.key ? "bg-primary" : "bg-muted-foreground/50"}`} />
                    {m.label}
                  </button>
                ))}
              </div>
              <Button
                data-testid="summarize-btn"
                onClick={runSummarize}
                disabled={busy}
                size="lg"
                className="rounded-full h-12 px-6"
              >
                {busy ? (<><Loader2 className="animate-spin mr-2" size={16} /> {t("dash.reading")}</>) : (<>{t("dash.summarise")} <ArrowRight size={16} className="ml-2" /></>)}
              </Button>
            </div>
            )}
          </div>

          {/* Result */}
          {busy && !current && (
            <div data-testid="skeleton-loader" className="rounded-3xl border border-border bg-card p-8 space-y-4">
              <div className="h-4 w-40 shimmer rounded-full" />
              <div className="h-8 w-3/4 shimmer rounded-lg" />
              <div className="h-4 w-full shimmer rounded-full" />
              <div className="h-4 w-11/12 shimmer rounded-full" />
              <div className="h-4 w-9/12 shimmer rounded-full" />
            </div>
          )}

          {current && <SummaryView summary={current} />}
        </section>

        {/* Right — history */}
        <aside className="lg:col-span-4">
          <div className="rounded-3xl border border-border bg-card p-6 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-primary" />
                <h2 className="text-lg font-display">{t("library.title")}</h2>
              </div>
              <span className="text-xs text-muted-foreground">{history.length}</span>
            </div>
            {history.length === 0 ? (
              <div data-testid="empty-history" className="text-sm text-muted-foreground py-6 text-center">
                {t("library.empty")}
              </div>
            ) : (
              <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="history-list">
                {history.map((s) => (
                  <li key={s.id} className="group">
                    <div className="rounded-xl border border-transparent hover:border-border hover:bg-accent/60 p-3 flex items-start gap-3 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        {s.mode === "url" && <Link2 size={14} />}
                        {s.mode === "pdf" && <FileText size={14} />}
                        {s.mode === "text" && <ScrollText size={14} />}
                        {s.mode === "youtube" && <Youtube size={14} />}
                        {s.mode === "meeting" && <Mic size={14} />}
                      </div>
                      <button
                        data-testid={`history-item-${s.id}`}
                        onClick={() => openSummary(s.id)}
                        className="flex-1 text-left"
                      >
                        <p className="text-sm line-clamp-2 leading-snug mb-1">{s.source_title}</p>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                          <Clock size={11} /> {new Date(s.created_at).toLocaleDateString()}
                        </p>
                      </button>
                      <button
                        data-testid={`delete-item-${s.id}`}
                        onClick={() => deleteSummary(s.id)}
                        className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-md flex items-center justify-center hover:bg-destructive/10 hover:text-destructive transition"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </main>

      {/* Floating chat bot (draggable, bottom-left by default) */}
      <FloatingBot context={current?.full_summary || null} />
    </div>
  );
}
